import { useCallback, useEffect, useState, MouseEvent } from 'react';
// @mui
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CardHeader from '@mui/material/CardHeader';
// luxon
import { DateTime } from 'luxon';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
// components
import { useSnackbar } from 'src/components/snackbar';
// redux
import { setError } from 'src/redux/slices/error';
import { useDispatch } from '../../../redux/store';
import { getCurrentMemberWaitingList } from '../../../redux/slices/members';
//
import { AccountDto, WaitingListService, WaitingListDto } from '../../../api';
import { useLocales } from '../../../locales';
import { MembershipWaitingListDialog } from './waiting-list-edit-dialog';

// ----------------------------------------------------------------------

type Props = {
  member: AccountDto;
  waitingList: WaitingListDto[];
};

export default function WaitingListCard({ member, waitingList }: Props) {
  const [waitingListId, setWaitingListId] = useState<number | undefined>();
  const { t } = useLocales();

  const popover = usePopover();

  const editDialogState = useBoolean();

  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const handleClose = useCallback(() => {
    popover.onClose();
  }, [popover]);

  const handleEdit = useCallback(() => {
    popover.onClose();
    editDialogState.onTrue();
  }, [popover, editDialogState]);

  const handleDelete = useCallback(async () => {
    if (!waitingListId) {
      enqueueSnackbar(t('No waiting list element selected!'), { variant: 'error' });
      return;
    }
    try {
      await WaitingListService.remove({ id: waitingListId });
      dispatch(getCurrentMemberWaitingList({ id: member.id! }));
    } catch (error) {
      enqueueSnackbar(t('Failed to remove from waiting list!'), { variant: 'error' });
      dispatch(setError(error));
      return;
    }
    enqueueSnackbar(t('Sucessfully removed from waiting list!'));
    dispatch(getCurrentMemberWaitingList({ id: member.id! }));
    handleClose();
  }, [waitingListId, handleClose, dispatch, enqueueSnackbar, member.id, t]);

  if (!waitingList) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader title={t('Waiting list')} />

        <Stack spacing={2.5} sx={{ p: 3 }}>
          {waitingList.map((x) => (
            <Stack
              key={`waiting-list-${x.id}`}
              variant="outlined"
              component={Paper}
              spacing={2}
              alignItems={{ md: 'flex-end' }}
              direction={{ xs: 'column', md: 'row' }}
              sx={{
                position: 'relative',
                p: 2.5,
                borderRadius: 1,
              }}
            >
              <Stack flexGrow={1} spacing={1}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle2">{x.membershipTypeName ?? ''}</Typography>
                </Stack>

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {`${t('Signed up at')}: ${(x.createdDate as DateTime).toLocaleString()}`}
                </Typography>
              </Stack>

              <IconButton
                onClick={(event: MouseEvent<HTMLElement>) => {
                  popover.onOpen(event);
                  setWaitingListId(x.id);
                }}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <Iconify icon="eva:more-vertical-fill" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Card>

      <CustomPopover open={popover.open} onClose={handleClose}>
        <MenuItem onClick={() => handleEdit()}>
          <Iconify icon="solar:pen-bold" />
          {t('Edit')}
        </MenuItem>

        <MenuItem onClick={() => handleDelete()} sx={{ color: 'error.main' }}>
          <Iconify icon="solar:trash-bin-trash-bold" />
          {t('Delete')}
        </MenuItem>
      </CustomPopover>
      <MembershipWaitingListDialog
        openState={editDialogState}
        member={member}
        membershipTypeId={waitingList.find((x) => x.id === waitingListId)?.membershipTypeId ?? 0}
        waitingListId={waitingListId ?? 0}
      />
    </>
  );
}
